import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { IntegrationPermission } from 'src/modules/shared/types/permissions.type';
import type { useNavigate } from '@tanstack/react-router';
import React from 'react';
import { flushSync } from 'react-dom';
import {
  IntegrationConnectionStatus,
  IntegrationProviderId,
  PropertyPortalPublicationConnectionState,
  PropertyPortalPublicationStatus,
  PropertyPortalPublicationRecordMode,
} from 'src/api/generated';
import type { useAlert } from 'src/contexts/alert-context';
import {
  type useCreateProperty,
  type useUpdateProperty,
} from 'src/modules/shared/hooks/api/catalog/properties.mutation';
import type { useProperty } from 'src/modules/shared/hooks/api/catalog/properties';
import type { useIntegrations } from 'src/modules/shared/hooks/api/integrations/integrations';
import { usePortalPublications } from 'src/modules/shared/hooks/api/integrations/portal-publications';
import type { useUnsavedNavigationGuardState } from 'src/modules/shared/hooks/unsaved-navigation-guard';
import type { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { getMeliIntegrationMode } from 'src/modules/shared/utils/integrations/meli-integration.util';
import { Route as CatalogPropertiesEditRoute } from 'src/routes/catalog/properties.$propertyId.edit';
import {
  buildPropertyMutationPayload,
  buildPropertyUpdateMutationPayload,
} from '../utils/property-form.util';
import { resolvePropertySourceProviderId } from '../utils/property-source-integration.util';
import type { PropertiesSearch } from '../schemas/properties-search.schema';
import type { PropertyFormValues } from '../validations/property-validations';

export type PropertyEditorPageState =
  | 'loading'
  | 'forbidden'
  | 'not_found'
  | 'ready';

const hasActiveSyncIntegration = (
  integrations: ReturnType<typeof useIntegrations>['integrations']
): boolean =>
  integrations?.items.some(({ connection, providerId }) => {
    if (connection?.status !== IntegrationConnectionStatus.ACTIVE) {
      return false;
    }

    if (providerId === IntegrationProviderId.MELI) {
      return getMeliIntegrationMode(connection.config ?? null) === 'SYNC';
    }

    return true;
  }) ?? false;

const getSourceIntegrationSyncManaged = (params: {
  propertySourceSystem: string | null | undefined;
  integrations: ReturnType<typeof useIntegrations>['integrations'];
}): boolean => {
  const sourceProviderId = resolvePropertySourceProviderId(
    params.propertySourceSystem
  );
  const sourceConnection =
    sourceProviderId != null
      ? params.integrations?.items.find(
          (item) => item.providerId === sourceProviderId
        )?.connection ?? null
      : null;

  return (
    sourceConnection?.status === IntegrationConnectionStatus.ACTIVE &&
    (sourceProviderId === 'meli'
      ? getMeliIntegrationMode(sourceConnection.config ?? null) === 'SYNC'
      : true)
  );
};

const getShouldRenderPublishingPanel = (params: {
  propertyExists: boolean;
  canReadPublications: boolean;
  isSourceIntegrationSyncManaged: boolean;
  persistedPublicationsCount: number;
  isMeliSyncMode: boolean;
  isMeliConnected: boolean;
  hasActivePropertySyncIntegration: boolean;
}): boolean =>
  params.propertyExists
    ? params.canReadPublications &&
      (params.isSourceIntegrationSyncManaged ||
        params.persistedPublicationsCount > 0 ||
        (!params.isMeliSyncMode && params.isMeliConnected))
    : params.canReadPublications && !params.hasActivePropertySyncIntegration;

const submitPropertyEditorForm = async (params: {
  isEdit: boolean;
  propertyId?: number;
  property: ReturnType<typeof useProperty>['property'];
  values: PropertyFormValues;
  createProperty: ReturnType<typeof useCreateProperty>['createProperty'];
  updateProperty: ReturnType<typeof useUpdateProperty>['updateProperty'];
  guard: ReturnType<typeof useUnsavedNavigationGuardState>;
  showAlert: ReturnType<typeof useAlert>['showAlert'];
  t: ReturnType<typeof useI18n>['t'];
  navigate: ReturnType<typeof useNavigate>;
  backSearch: PropertiesSearch;
}): Promise<void> => {
  if (params.isEdit) {
    if (params.propertyId == null) {
      throw new Error('Property id is required to update a property.');
    }

    await params.updateProperty({
      id: params.propertyId,
      ...buildPropertyUpdateMutationPayload(params.values, params.property),
    });
    flushSync(() => {
      params.guard.setHasUnsavedChanges(false);
    });
    params.showAlert(
      params.t('catalog.properties.alert.update.success'),
      'success'
    );
    return;
  }

  const payload = buildPropertyMutationPayload(params.values);
  const createdProperty = await params.createProperty(payload);
  flushSync(() => {
    params.guard.setHasUnsavedChanges(false);
  });
  params.showAlert(
    params.t('catalog.properties.alert.create.success'),
    'success'
  );

  void params.navigate({
    to: CatalogPropertiesEditRoute.to,
    params: { propertyId: createdProperty.id },
    replace: true,
    search: params.backSearch,
  });
};

export const usePropertyEditorPublishingState = (params: {
  property: ReturnType<typeof useProperty>['property'];
  integrations: ReturnType<typeof useIntegrations>['integrations'];
}) => {
  const { hasPermission: canReadPublications } = useHasPermissions(
    IntegrationPermission.Read
  );
  const meliConnection = React.useMemo(
    () =>
      params.integrations?.items.find(
        (item) => item.providerId === IntegrationProviderId.MELI
      )?.connection ?? null,
    [params.integrations]
  );
  const isMeliConnected =
    meliConnection?.status === IntegrationConnectionStatus.ACTIVE;
  const isMeliSyncMode =
    isMeliConnected &&
    getMeliIntegrationMode(meliConnection.config ?? null) === 'SYNC';
  const portalFilterBy = React.useMemo(() => {
    if (!params.property) {
      return undefined;
    }

    return createQuery()
      .equals('propertyId', params.property.id)
      .equals('portal', 'meli')
      .take(20)
      .build().filterBy;
  }, [params.property]);
  const {
    publications: persistedPublications,
    loading: loadingPublications,
    error: publicationsError,
  } = usePortalPublications({
    filterBy: portalFilterBy,
    enabled: params.property != null && canReadPublications,
  });
  const hasActivePropertySyncIntegration = hasActiveSyncIntegration(
    params.integrations
  );
  const isSourceIntegrationSyncManaged = getSourceIntegrationSyncManaged({
    propertySourceSystem: params.property?.sourceSystem,
    integrations: params.integrations,
  });
  const shouldRenderPublishingPanel = getShouldRenderPublishingPanel({
    propertyExists: params.property != null,
    canReadPublications,
    isSourceIntegrationSyncManaged,
    persistedPublicationsCount: persistedPublications.length,
    isMeliSyncMode,
    isMeliConnected,
    hasActivePropertySyncIntegration,
  });
  const republishablePublications = React.useMemo(
    () =>
      (canReadPublications && !loadingPublications && !publicationsError
        ? persistedPublications
        : params.property?.publications ?? []
      ).filter(
        (publication) =>
          publication.connectionState ===
            PropertyPortalPublicationConnectionState.CONNECTED &&
          (publication.recordMode ===
            PropertyPortalPublicationRecordMode.MANAGED_PUBLISH ||
            publication.recordMode ===
              PropertyPortalPublicationRecordMode.MANAGED_SYNC) &&
          publication.status === PropertyPortalPublicationStatus.PUBLISHED &&
          typeof publication.externalListingId === 'string' &&
          publication.externalListingId.trim().length > 0
      ),
    [
      canReadPublications,
      params.property,
      loadingPublications,
      publicationsError,
      persistedPublications,
    ]
  );

  return {
    loadingPublications,
    hasActivePropertySyncIntegration,
    shouldRenderPublishingPanel,
    republishablePublicationCount: republishablePublications.length,
  };
};

export const usePropertyEditorSubmitFlow = (params: {
  isEdit: boolean;
  propertyId?: number;
  property: ReturnType<typeof useProperty>['property'];
  republishablePublicationCount: number;
  createProperty: ReturnType<typeof useCreateProperty>['createProperty'];
  updateProperty: ReturnType<typeof useUpdateProperty>['updateProperty'];
  guard: ReturnType<typeof useUnsavedNavigationGuardState>;
  showAlert: ReturnType<typeof useAlert>['showAlert'];
  t: ReturnType<typeof useI18n>['t'];
  navigate: ReturnType<typeof useNavigate>;
  backSearch: PropertiesSearch;
}) => {
  const [pendingSubmitValues, setPendingSubmitValues] =
    React.useState<PropertyFormValues | null>(null);

  const isPendingDeactivation =
    params.isEdit &&
    params.property?.isActive === true &&
    pendingSubmitValues?.isActive === false;

  const executeSubmit = React.useCallback(
    async (values: PropertyFormValues) => {
      try {
        await submitPropertyEditorForm({
          isEdit: params.isEdit,
          propertyId: params.propertyId,
          property: params.property,
          values,
          createProperty: params.createProperty,
          updateProperty: params.updateProperty,
          guard: params.guard,
          showAlert: params.showAlert,
          t: params.t,
          navigate: params.navigate,
          backSearch: params.backSearch,
        });
      } catch {
        params.showAlert(
          params.t(
            params.isEdit
              ? 'catalog.properties.alert.update.error'
              : 'catalog.properties.alert.create.error'
          ),
          'error'
        );
      }
    },
    [params]
  );

  const handleSubmitWithErrors = React.useCallback(
    async (values: PropertyFormValues) => {
      const isDeactivationTransition =
        params.isEdit &&
        params.property?.isActive === true &&
        values.isActive === false;

      if (
        isDeactivationTransition ||
        (params.isEdit && params.republishablePublicationCount > 0)
      ) {
        setPendingSubmitValues(values);
        return;
      }

      await executeSubmit(values);
    },
    [executeSubmit, params]
  );

  const handleConfirmPendingSubmit = React.useCallback(() => {
    if (pendingSubmitValues == null) {
      return;
    }

    void executeSubmit(pendingSubmitValues).finally(() => {
      setPendingSubmitValues(null);
    });
  }, [executeSubmit, pendingSubmitValues]);

  return {
    pendingSubmitValues,
    setPendingSubmitValues,
    isPendingDeactivation,
    handleSubmitWithErrors,
    handleConfirmPendingSubmit,
  };
};

export const getPropertyEditorPageState = (params: {
  loadingPropertyPermissions: boolean;
  loadingPropertyCreate: boolean;
  loadingPropertyUpdate: boolean;
  loadingPublicationPermissions: boolean;
  loadingProperty: boolean;
  loadingIntegrations: boolean;
  loadingPublications: boolean;
  property: ReturnType<typeof useProperty>['property'];
  isEdit: boolean;
  canReadProperties: boolean;
  propertyError: unknown;
}): PropertyEditorPageState => {
  if (
    params.loadingPropertyPermissions ||
    params.loadingPropertyCreate ||
    params.loadingPropertyUpdate ||
    params.loadingPublicationPermissions ||
    params.loadingProperty ||
    params.loadingIntegrations ||
    (params.property != null && params.loadingPublications)
  ) {
    return 'loading';
  }

  if (!params.canReadProperties) {
    return 'forbidden';
  }

  if (params.propertyError || (params.isEdit && !params.property)) {
    return 'not_found';
  }

  return 'ready';
};
